import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BusinessOperationTraceV1,
  BusinessOperationTraceV1Schema,
  isBusinessOperationTraceV1,
} from "./business-operation.js";
import {
  type InventoryLotReceiptAllocationRecord,
  InventoryLotReceiptAllocationRecordSchema,
  InventoryLotReceiptAllocationRequestSchema,
  isInventoryLotReceiptAllocationRecord,
} from "./inventory-lot.js";
import { PurchaseOrderSupplierSnapshotSchema } from "./procurement.js";
import { PurchaseOrderNumberSchema } from "./purchase-order-number.js";
import {
  derivePurchaseOrderShortCloseNumber,
  PurchaseOrderShortCloseNumberSchema,
} from "./purchase-order-short-close-number.js";
import {
  derivePurchaseReceiptNumber,
  PurchaseReceiptNumberSchema,
} from "./purchase-receipt-number.js";

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const LineIdSchema = Type.String({
  minLength: 1,
  maxLength: 64,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$",
});
const CanonicalTextPattern = "^(?:\\S|\\S[\\s\\S]*\\S)$";
const CanonicalTextSchema = (maximum: number) =>
  Type.String({ minLength: 1, maxLength: maximum, pattern: CanonicalTextPattern });
const NullableCanonicalTextSchema = (maximum: number) =>
  Type.Union([CanonicalTextSchema(maximum), Type.Null()]);
const BusinessDateSchema = Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" });
const TimestampSchema = Type.String({ minLength: 20, maxLength: 64 });
const VersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const QuantitySchema = Type.Integer({ minimum: 1, maximum: 1_000_000_000_000 });
const NonNegativeQuantitySchema = Type.Integer({ minimum: 0, maximum: 1_000_000_000_000 });
// A purchase order may contain 200 independently valid lines. Whole-order
// totals therefore cannot reuse the per-line quantity ceiling.
const AggregateQuantitySchema = Type.Integer({
  minimum: 0,
  maximum: 200_000_000_000_000,
});
const AmountFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const Sha256Schema = Type.String({ pattern: "^[a-f0-9]{64}$" });
const IdempotencyKeySchema = Type.String({
  minLength: 8,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$",
});

export const PurchaseReceiptLineRequestSchema = Type.Object(
  {
    lineId: LineIdSchema,
    receivedBaseQuantity: QuantitySchema,
    lotAllocations: Type.Optional(
      Type.Array(InventoryLotReceiptAllocationRequestSchema, { minItems: 1, maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type PurchaseReceiptLineRequest = Static<typeof PurchaseReceiptLineRequestSchema>;

export const RecordPurchaseReceiptRequestSchema = Type.Object(
  {
    receiptId: StableIdSchema,
    purchaseOrderId: StableIdSchema,
    expectedPurchaseOrderVersion: VersionSchema,
    businessDate: BusinessDateSchema,
    notes: NullableCanonicalTextSchema(500),
    lines: Type.Array(PurchaseReceiptLineRequestSchema, { minItems: 1, maxItems: 200 }),
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type RecordPurchaseReceiptRequest = Static<typeof RecordPurchaseReceiptRequestSchema>;

export const ClosePurchaseOrderShortRequestSchema = Type.Object(
  {
    shortCloseId: StableIdSchema,
    purchaseOrderId: StableIdSchema,
    businessDate: BusinessDateSchema,
    expectedPurchaseOrderVersion: VersionSchema,
    reason: CanonicalTextSchema(200),
    operationTrace: BusinessOperationTraceV1Schema,
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type ClosePurchaseOrderShortRequest = Static<typeof ClosePurchaseOrderShortRequestSchema>;

export const PurchaseInventoryValuationSnapshotSchema = Type.Object(
  {
    quantity: NonNegativeQuantitySchema,
    totalValueFen: AmountFenSchema,
    averageUnitCost: Type.Union([
      Type.Object(
        {
          floorFen: AmountFenSchema,
          remainderFen: AmountFenSchema,
          denominator: QuantitySchema,
        },
        { additionalProperties: false },
      ),
      Type.Null(),
    ]),
  },
  { additionalProperties: false },
);
export type PurchaseInventoryValuationSnapshot = Static<
  typeof PurchaseInventoryValuationSnapshotSchema
>;

export const PurchaseInventoryValuationSourceSchema = Type.Union([
  Type.Object({ kind: Type.Literal("EMPTY") }, { additionalProperties: false }),
  Type.Object(
    {
      kind: Type.Literal("OPENING_PRODUCT_COST"),
      productVersion: VersionSchema,
      unitCostFen: AmountFenSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("PURCHASE_RECEIPT"),
      receiptId: StableIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("PURCHASE_RETURN"),
      returnId: StableIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("SUPPLIER_CREDIT"),
      creditId: StableIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("TRACKED_SALE"),
      orderItemId: StableIdSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("INVENTORY_COUNT"),
      approvalId: StableIdSchema,
      countNumber: Type.String({ pattern: "^PD-[0-9]{8}-[0-9A-F]{12}$" }),
    },
    { additionalProperties: false },
  ),
]);
export type PurchaseInventoryValuationSource = Static<
  typeof PurchaseInventoryValuationSourceSchema
>;

export const PurchaseReceiptLineRecordSchema = Type.Object(
  {
    lineId: LineIdSchema,
    movementId: StableIdSchema,
    skuId: LineIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    inputUnitName: CanonicalTextSchema(16),
    orderedInputUnitQuantity: QuantitySchema,
    baseUnitName: CanonicalTextSchema(16),
    baseUnitQuantityPerInput: QuantitySchema,
    conversionId: Type.Union([LineIdSchema, Type.Null()]),
    conversionVersion: Type.Union([VersionSchema, Type.Null()]),
    orderedBaseQuantity: QuantitySchema,
    orderedSubtotalFen: AmountFenSchema,
    orderedTaxFen: AmountFenSchema,
    orderedTotalFen: AmountFenSchema,
    receivedBaseQuantityBefore: NonNegativeQuantitySchema,
    receivedBaseQuantity: QuantitySchema,
    receivedBaseQuantityAfter: QuantitySchema,
    remainingBaseQuantityAfter: NonNegativeQuantitySchema,
    subtotalFen: AmountFenSchema,
    taxFen: AmountFenSchema,
    totalFen: AmountFenSchema,
    inventoryValueIncreaseFen: AmountFenSchema,
    inventoryQuantityBefore: NonNegativeQuantitySchema,
    inventoryQuantityAfter: QuantitySchema,
    inventoryVersionBefore: NonNegativeQuantitySchema,
    inventoryVersionAfter: VersionSchema,
    valuationBefore: PurchaseInventoryValuationSnapshotSchema,
    valuationBeforeSource: PurchaseInventoryValuationSourceSchema,
    valuationAfter: PurchaseInventoryValuationSnapshotSchema,
    valuationAfterSource: Type.Object(
      {
        kind: Type.Literal("PURCHASE_RECEIPT"),
        receiptId: StableIdSchema,
      },
      { additionalProperties: false },
    ),
    lotAllocations: Type.Optional(
      Type.Array(InventoryLotReceiptAllocationRecordSchema, { minItems: 1, maxItems: 100 }),
    ),
  },
  { additionalProperties: false },
);
export type PurchaseReceiptLineRecord = Static<typeof PurchaseReceiptLineRecordSchema>;

export const PurchaseReceiptRecordSchema = Type.Object(
  {
    receiptId: StableIdSchema,
    receiptNumber: PurchaseReceiptNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    approvalId: StableIdSchema,
    approvalSnapshotSha256: Sha256Schema,
    purchaseOrderVersionBefore: VersionSchema,
    purchaseOrderVersionAfter: VersionSchema,
    purchaseOrderStatusAfter: Type.Union([
      Type.Literal("PARTIALLY_RECEIVED"),
      Type.Literal("RECEIVED"),
    ]),
    purchaseOrderRemainingBaseQuantityAfter: AggregateQuantitySchema,
    businessDate: BusinessDateSchema,
    supplier: PurchaseOrderSupplierSnapshotSchema,
    notes: NullableCanonicalTextSchema(500),
    lines: Type.Array(PurchaseReceiptLineRecordSchema, { minItems: 1, maxItems: 200 }),
    subtotalFen: AmountFenSchema,
    taxFen: AmountFenSchema,
    totalFen: AmountFenSchema,
    inventoryEffect: Type.Union([Type.Literal("PARTIAL"), Type.Literal("FULL")]),
    payableEffect: Type.Literal("OPEN"),
    payableTotalFenBefore: AmountFenSchema,
    payableTotalFenAfter: AmountFenSchema,
    receiverActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    receivedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type PurchaseReceiptRecord = Static<typeof PurchaseReceiptRecordSchema>;

export const PurchaseReceiptProjectionStatusSchema = Type.Union([
  Type.Literal("PENDING"),
  Type.Literal("SYNCED"),
  Type.Literal("NEEDS_REVIEW"),
]);
export type PurchaseReceiptProjectionStatus = Static<typeof PurchaseReceiptProjectionStatusSchema>;

export const PurchaseReceiptWriteResultSchema = Type.Object(
  {
    receipt: PurchaseReceiptRecordSchema,
    cloudProjectionStatus: PurchaseReceiptProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type PurchaseReceiptWriteResult = Static<typeof PurchaseReceiptWriteResultSchema>;

export const PurchaseOrderReceivingLineReadModelSchema = Type.Object(
  {
    lineId: LineIdSchema,
    skuId: LineIdSchema,
    productName: CanonicalTextSchema(80),
    productVersion: VersionSchema,
    inputUnitName: CanonicalTextSchema(16),
    inputUnitQuantity: QuantitySchema,
    baseUnitName: CanonicalTextSchema(16),
    baseUnitQuantityPerInput: QuantitySchema,
    conversionId: Type.Union([LineIdSchema, Type.Null()]),
    conversionVersion: Type.Union([VersionSchema, Type.Null()]),
    orderedBaseQuantity: QuantitySchema,
    receivedBaseQuantity: NonNegativeQuantitySchema,
    remainingBaseQuantity: NonNegativeQuantitySchema,
    orderedSubtotalFen: AmountFenSchema,
    orderedTaxFen: AmountFenSchema,
    orderedTotalFen: AmountFenSchema,
    receivedSubtotalFen: AmountFenSchema,
    receivedTaxFen: AmountFenSchema,
    receivedTotalFen: AmountFenSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderReceivingLineReadModel = Static<
  typeof PurchaseOrderReceivingLineReadModelSchema
>;

export type PurchaseReceiptEntryUnit = "INPUT_UNIT" | "BASE_UNIT";

export interface PurchaseReceiptQuantityEntry {
  unit: PurchaseReceiptEntryUnit;
  quantity: number;
}

export function derivePurchaseReceiptBaseQuantity(
  line: Pick<
    PurchaseOrderReceivingLineReadModel,
    "baseUnitQuantityPerInput" | "remainingBaseQuantity"
  >,
  entry: PurchaseReceiptQuantityEntry,
): number {
  if (
    (entry.unit !== "INPUT_UNIT" && entry.unit !== "BASE_UNIT") ||
    !Number.isSafeInteger(entry.quantity) ||
    entry.quantity < 1 ||
    entry.quantity > 1_000_000 ||
    !Number.isSafeInteger(line.baseUnitQuantityPerInput) ||
    line.baseUnitQuantityPerInput < 1 ||
    !Number.isSafeInteger(line.remainingBaseQuantity) ||
    line.remainingBaseQuantity < 0
  ) {
    throw new RangeError("purchase receipt quantity entry is outside the safe range");
  }
  const multiplier = entry.unit === "INPUT_UNIT" ? line.baseUnitQuantityPerInput : 1;
  const receivedBaseQuantity = entry.quantity * multiplier;
  if (
    !Number.isSafeInteger(receivedBaseQuantity) ||
    receivedBaseQuantity < 1 ||
    receivedBaseQuantity > 1_000_000 ||
    receivedBaseQuantity > line.remainingBaseQuantity
  ) {
    throw new RangeError("purchase receipt base quantity is outside the remaining safe range");
  }
  return receivedBaseQuantity;
}

export function restorePurchaseReceiptQuantityEntry(
  line: Pick<PurchaseOrderReceivingLineReadModel, "baseUnitQuantityPerInput" | "conversionId">,
  receivedBaseQuantity: number,
): PurchaseReceiptQuantityEntry {
  if (
    !Number.isSafeInteger(receivedBaseQuantity) ||
    receivedBaseQuantity < 1 ||
    receivedBaseQuantity > 1_000_000 ||
    !Number.isSafeInteger(line.baseUnitQuantityPerInput) ||
    line.baseUnitQuantityPerInput < 1
  ) {
    throw new RangeError("purchase receipt recovery quantity is outside the remaining safe range");
  }
  if (line.conversionId !== null && receivedBaseQuantity % line.baseUnitQuantityPerInput === 0) {
    return {
      unit: "INPUT_UNIT",
      quantity: receivedBaseQuantity / line.baseUnitQuantityPerInput,
    };
  }
  return { unit: "BASE_UNIT", quantity: receivedBaseQuantity };
}

export const PurchaseOrderShortCloseLineRecordSchema = Type.Object(
  {
    lineId: LineIdSchema,
    skuId: LineIdSchema,
    productName: CanonicalTextSchema(80),
    baseUnitName: CanonicalTextSchema(16),
    orderedBaseQuantity: QuantitySchema,
    receivedBaseQuantity: NonNegativeQuantitySchema,
    shortClosedBaseQuantity: NonNegativeQuantitySchema,
    orderedSubtotalFen: AmountFenSchema,
    orderedTaxFen: AmountFenSchema,
    orderedTotalFen: AmountFenSchema,
    receivedSubtotalFen: AmountFenSchema,
    receivedTaxFen: AmountFenSchema,
    receivedTotalFen: AmountFenSchema,
    shortClosedSubtotalFen: AmountFenSchema,
    shortClosedTaxFen: AmountFenSchema,
    shortClosedTotalFen: AmountFenSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderShortCloseLineRecord = Static<
  typeof PurchaseOrderShortCloseLineRecordSchema
>;

export const PurchaseOrderShortCloseRecordSchema = Type.Object(
  {
    shortCloseId: StableIdSchema,
    shortCloseNumber: PurchaseOrderShortCloseNumberSchema,
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    approvalId: StableIdSchema,
    approvalSnapshotSha256: Sha256Schema,
    purchaseOrderVersionBefore: VersionSchema,
    purchaseOrderVersionAfter: VersionSchema,
    purchaseOrderStatusBefore: Type.Literal("PARTIALLY_RECEIVED"),
    purchaseOrderStatusAfter: Type.Literal("CLOSED"),
    businessDate: BusinessDateSchema,
    lastReceiptBusinessDate: BusinessDateSchema,
    supplier: PurchaseOrderSupplierSnapshotSchema,
    reason: CanonicalTextSchema(200),
    lines: Type.Array(PurchaseOrderShortCloseLineRecordSchema, {
      minItems: 1,
      maxItems: 200,
    }),
    shortClosedBaseQuantity: QuantitySchema,
    shortClosedSubtotalFen: AmountFenSchema,
    shortClosedTaxFen: AmountFenSchema,
    shortClosedTotalFen: AmountFenSchema,
    inventoryEffect: Type.Literal("NONE"),
    payableEffect: Type.Literal("NONE"),
    payableTotalFenBefore: AmountFenSchema,
    payableTotalFenAfter: AmountFenSchema,
    closerActorId: StableIdSchema,
    operationTrace: BusinessOperationTraceV1Schema,
    closedAt: TimestampSchema,
    evidence: Type.Literal("STORE_ACCEPTED"),
  },
  { additionalProperties: false },
);
export type PurchaseOrderShortCloseRecord = Static<typeof PurchaseOrderShortCloseRecordSchema>;

export const PurchaseOrderShortCloseWriteResultSchema = Type.Object(
  {
    shortClose: PurchaseOrderShortCloseRecordSchema,
    cloudProjectionStatus: PurchaseReceiptProjectionStatusSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderShortCloseWriteResult = Static<
  typeof PurchaseOrderShortCloseWriteResultSchema
>;

export const PurchaseOrderReceivingWorkspaceSchema = Type.Object(
  {
    purchaseOrderId: StableIdSchema,
    purchaseOrderNumber: PurchaseOrderNumberSchema,
    approvalId: StableIdSchema,
    approvalSnapshotSha256: Sha256Schema,
    purchaseOrderVersion: VersionSchema,
    lastCommandId: StableIdSchema,
    status: Type.Union([
      Type.Literal("APPROVED"),
      Type.Literal("PARTIALLY_RECEIVED"),
      Type.Literal("RECEIVED"),
      Type.Literal("CLOSED"),
    ]),
    businessDate: BusinessDateSchema,
    supplier: PurchaseOrderSupplierSnapshotSchema,
    lines: Type.Array(PurchaseOrderReceivingLineReadModelSchema, { minItems: 1, maxItems: 200 }),
    orderedSubtotalFen: AmountFenSchema,
    orderedTaxFen: AmountFenSchema,
    orderedTotalFen: AmountFenSchema,
    receivedSubtotalFen: AmountFenSchema,
    receivedTaxFen: AmountFenSchema,
    receivedTotalFen: AmountFenSchema,
    payableTotalFen: AmountFenSchema,
    receipts: Type.Array(PurchaseReceiptWriteResultSchema, { maxItems: 500 }),
    shortClose: Type.Union([PurchaseOrderShortCloseRecordSchema, Type.Null()]),
    canReceive: Type.Boolean(),
    canCloseShort: Type.Boolean(),
    blocker: Type.Union([CanonicalTextSchema(300), Type.Null()]),
    generatedAt: TimestampSchema,
  },
  { additionalProperties: false },
);
export type PurchaseOrderReceivingWorkspace = Static<typeof PurchaseOrderReceivingWorkspaceSchema>;
export const PurchaseOrderReceivingWorkspaceListSchema = Type.Array(
  PurchaseOrderReceivingWorkspaceSchema,
  { maxItems: 500 },
);

export const PurchaseReceiptBusinessEventPayloadSchema = Type.Object(
  {
    receipt: PurchaseReceiptRecordSchema,
  },
  { additionalProperties: false },
);
export type PurchaseReceiptBusinessEventPayload = Static<
  typeof PurchaseReceiptBusinessEventPayloadSchema
>;

export const PurchaseOrderShortClosedBusinessEventPayloadSchema = Type.Object(
  { shortClose: PurchaseOrderShortCloseRecordSchema },
  { additionalProperties: false },
);
export type PurchaseOrderShortClosedBusinessEventPayload = Static<
  typeof PurchaseOrderShortClosedBusinessEventPayloadSchema
>;

function isCanonicalBusinessDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString() === `${value}T00:00:00.000Z`
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function safeAdd(values: readonly number[]): number | null {
  let total = 0;
  for (const value of values) {
    if (!Number.isSafeInteger(value)) return null;
    total += value;
    if (!Number.isSafeInteger(total)) return null;
  }
  return total;
}

export function allocatePurchaseReceiptFen(
  orderedAmountFen: number,
  orderedBaseQuantity: number,
  receivedBaseQuantityBefore: number,
  receivedBaseQuantityAfter: number,
): number {
  if (
    !Number.isSafeInteger(orderedAmountFen) ||
    orderedAmountFen < 0 ||
    !Number.isSafeInteger(orderedBaseQuantity) ||
    orderedBaseQuantity <= 0 ||
    !Number.isSafeInteger(receivedBaseQuantityBefore) ||
    receivedBaseQuantityBefore < 0 ||
    !Number.isSafeInteger(receivedBaseQuantityAfter) ||
    receivedBaseQuantityAfter <= receivedBaseQuantityBefore ||
    receivedBaseQuantityAfter > orderedBaseQuantity
  ) {
    throw new RangeError(
      "purchase receipt allocation inputs are outside the safe cumulative range",
    );
  }
  const total = BigInt(orderedAmountFen);
  const denominator = BigInt(orderedBaseQuantity);
  const before = (total * BigInt(receivedBaseQuantityBefore)) / denominator;
  const after = (total * BigInt(receivedBaseQuantityAfter)) / denominator;
  return Number(after - before);
}

export function purchaseInventoryValuationSnapshot(
  quantity: number,
  totalValueFen: number,
): PurchaseInventoryValuationSnapshot {
  if (
    !Number.isSafeInteger(quantity) ||
    quantity < 0 ||
    !Number.isSafeInteger(totalValueFen) ||
    totalValueFen < 0 ||
    (quantity === 0 && totalValueFen !== 0)
  ) {
    throw new RangeError("inventory valuation inputs are invalid");
  }
  if (quantity === 0) return { quantity, totalValueFen, averageUnitCost: null };
  return {
    quantity,
    totalValueFen,
    averageUnitCost: {
      floorFen: Math.floor(totalValueFen / quantity),
      remainderFen: totalValueFen % quantity,
      denominator: quantity,
    },
  };
}

export function isPurchaseInventoryValuationSnapshot(
  value: unknown,
): value is PurchaseInventoryValuationSnapshot {
  if (!Value.Check(PurchaseInventoryValuationSnapshotSchema, value)) return false;
  const snapshot = value as PurchaseInventoryValuationSnapshot;
  if (snapshot.quantity === 0) {
    return snapshot.totalValueFen === 0 && snapshot.averageUnitCost === null;
  }
  const average = snapshot.averageUnitCost;
  return (
    average !== null &&
    average.denominator === snapshot.quantity &&
    average.remainderFen < average.denominator &&
    Number.isSafeInteger(average.floorFen * average.denominator + average.remainderFen) &&
    average.floorFen * average.denominator + average.remainderFen === snapshot.totalValueFen
  );
}

function valuationSourceMatchesBefore(
  source: PurchaseInventoryValuationSource,
  snapshot: PurchaseInventoryValuationSnapshot,
): boolean {
  if (source.kind === "EMPTY") {
    return snapshot.quantity === 0 && snapshot.totalValueFen === 0;
  }
  if (source.kind === "PURCHASE_RECEIPT") return snapshot.quantity > 0;
  if (
    source.kind === "PURCHASE_RETURN" ||
    source.kind === "SUPPLIER_CREDIT" ||
    source.kind === "TRACKED_SALE" ||
    source.kind === "INVENTORY_COUNT"
  ) {
    return snapshot.quantity >= 0;
  }
  const totalValueFen = source.unitCostFen * snapshot.quantity;
  return Number.isSafeInteger(totalValueFen) && totalValueFen === snapshot.totalValueFen;
}

function traceMatchesPurchaseOrder(
  trace: BusinessOperationTraceV1,
  purchaseOrderId: string,
  purchaseOrderNumber: string,
  idempotencyKey: string,
): boolean {
  return (
    isBusinessOperationTraceV1(trace) &&
    trace.commandId === idempotencyKey &&
    trace.idempotencyKey === idempotencyKey &&
    trace.correlationId === `purchase-order:${purchaseOrderId}` &&
    trace.causationId !== null &&
    trace.businessNumber?.kind === "PURCHASE_ORDER" &&
    trace.businessNumber.value === purchaseOrderNumber
  );
}

/**
 * Mandatory state-aware receipt gate. Persistence writers must invoke this
 * while the current purchase order receiving workspace is locked. The
 * request-only guard cannot prove the current version, direct causal parent,
 * public number, or remaining quantities.
 */
export function isRecordPurchaseReceiptAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is RecordPurchaseReceiptRequest {
  if (
    !isRecordPurchaseReceiptRequest(requestValue) ||
    !isPurchaseOrderReceivingWorkspace(workspaceValue)
  ) {
    return false;
  }
  const request = requestValue as RecordPurchaseReceiptRequest;
  const workspace = workspaceValue as PurchaseOrderReceivingWorkspace;
  if (
    request.purchaseOrderId !== workspace.purchaseOrderId ||
    request.expectedPurchaseOrderVersion !== workspace.purchaseOrderVersion ||
    !workspace.canReceive ||
    workspace.status === "RECEIVED" ||
    workspace.status === "CLOSED" ||
    request.operationTrace.causationId !== workspace.lastCommandId ||
    !traceMatchesPurchaseOrder(
      request.operationTrace,
      workspace.purchaseOrderId,
      workspace.purchaseOrderNumber,
      request.idempotencyKey,
    )
  ) {
    return false;
  }
  const lineById = new Map(workspace.lines.map((line) => [line.lineId, line]));
  return request.lines.every((line) => {
    const current = lineById.get(line.lineId);
    return current !== undefined && line.receivedBaseQuantity <= current.remainingBaseQuantity;
  });
}

export function isRecordPurchaseReceiptRequest(
  value: unknown,
): value is RecordPurchaseReceiptRequest {
  if (!Value.Check(RecordPurchaseReceiptRequestSchema, value)) return false;
  const request = value as RecordPurchaseReceiptRequest;
  if (
    !isCanonicalBusinessDate(request.businessDate) ||
    (request.notes !== null && request.notes !== request.notes.trim()) ||
    request.operationTrace.commandId !== request.idempotencyKey ||
    request.operationTrace.idempotencyKey !== request.idempotencyKey ||
    request.operationTrace.correlationId !== `purchase-order:${request.purchaseOrderId}` ||
    request.operationTrace.causationId === null ||
    request.operationTrace.businessNumber?.kind !== "PURCHASE_ORDER"
  ) {
    return false;
  }
  const lineIds = new Set(request.lines.map((line) => line.lineId));
  return (
    lineIds.size === request.lines.length &&
    request.lines.every((line) => {
      if (line.lotAllocations === undefined) return true;
      const lotIds = new Set(line.lotAllocations.map((allocation) => allocation.lotId));
      const lotNumbers = new Set(
        line.lotAllocations.map((allocation) => allocation.lotNumber.toLocaleLowerCase("zh-CN")),
      );
      const lotQuantity = safeAdd(
        line.lotAllocations.map((allocation) => allocation.receivedBaseQuantity),
      );
      return (
        lotIds.size === line.lotAllocations.length &&
        lotNumbers.size === line.lotAllocations.length &&
        lotQuantity === line.receivedBaseQuantity &&
        line.lotAllocations.every(
          (allocation) =>
            allocation.lotNumber === allocation.lotNumber.trim() &&
            (allocation.expiryDate === null || isCanonicalBusinessDate(allocation.expiryDate)),
        )
      );
    })
  );
}

export function isClosePurchaseOrderShortRequest(
  value: unknown,
): value is ClosePurchaseOrderShortRequest {
  if (!Value.Check(ClosePurchaseOrderShortRequestSchema, value)) return false;
  const request = value as ClosePurchaseOrderShortRequest;
  try {
    derivePurchaseOrderShortCloseNumber(request.businessDate, request.shortCloseId);
  } catch {
    return false;
  }
  return (
    isCanonicalBusinessDate(request.businessDate) &&
    request.reason === request.reason.trim() &&
    request.operationTrace.commandId === request.idempotencyKey &&
    request.operationTrace.idempotencyKey === request.idempotencyKey &&
    request.operationTrace.correlationId === `purchase-order:${request.purchaseOrderId}` &&
    request.operationTrace.causationId !== null &&
    request.operationTrace.businessNumber?.kind === "PURCHASE_ORDER"
  );
}

export function isClosePurchaseOrderShortAgainstWorkspace(
  requestValue: unknown,
  workspaceValue: unknown,
): requestValue is ClosePurchaseOrderShortRequest {
  if (
    !isClosePurchaseOrderShortRequest(requestValue) ||
    !isPurchaseOrderReceivingWorkspace(workspaceValue)
  ) {
    return false;
  }
  const request = requestValue as ClosePurchaseOrderShortRequest;
  const workspace = workspaceValue as PurchaseOrderReceivingWorkspace;
  const latestReceiptBusinessDate = workspace.receipts.reduce(
    (latest, item) => (item.receipt.businessDate > latest ? item.receipt.businessDate : latest),
    workspace.businessDate,
  );
  return (
    workspace.status === "PARTIALLY_RECEIVED" &&
    workspace.shortClose === null &&
    workspace.canCloseShort &&
    request.purchaseOrderId === workspace.purchaseOrderId &&
    request.businessDate >= latestReceiptBusinessDate &&
    request.expectedPurchaseOrderVersion === workspace.purchaseOrderVersion &&
    request.operationTrace.causationId === workspace.lastCommandId &&
    traceMatchesPurchaseOrder(
      request.operationTrace,
      workspace.purchaseOrderId,
      workspace.purchaseOrderNumber,
      request.idempotencyKey,
    )
  );
}

export function isPurchaseReceiptRecord(value: unknown): value is PurchaseReceiptRecord {
  if (!Value.Check(PurchaseReceiptRecordSchema, value)) return false;
  const receipt = value as PurchaseReceiptRecord;
  let expectedReceiptNumber: string;
  try {
    expectedReceiptNumber = derivePurchaseReceiptNumber(receipt.businessDate, receipt.receiptId);
  } catch {
    return false;
  }
  if (
    receipt.receiptNumber !== expectedReceiptNumber ||
    receipt.purchaseOrderVersionAfter !== receipt.purchaseOrderVersionBefore + 1 ||
    !isCanonicalBusinessDate(receipt.businessDate) ||
    !isCanonicalTimestamp(receipt.receivedAt) ||
    (receipt.notes !== null && receipt.notes !== receipt.notes.trim()) ||
    !traceMatchesPurchaseOrder(
      receipt.operationTrace,
      receipt.purchaseOrderId,
      receipt.purchaseOrderNumber,
      receipt.operationTrace.idempotencyKey,
    ) ||
    receipt.payableTotalFenAfter !== receipt.payableTotalFenBefore + receipt.totalFen
  ) {
    return false;
  }
  const lineIds = new Set<string>();
  const movementIds = new Set<string>();
  let remaining = 0;
  for (const line of receipt.lines) {
    const lotAllocations = line.lotAllocations as InventoryLotReceiptAllocationRecord[] | undefined;
    const lotIds = new Set(lotAllocations?.map((allocation) => allocation.lotId) ?? []);
    const lotNumbers = new Set(
      lotAllocations?.map((allocation) => allocation.lotNumber.toLocaleLowerCase("zh-CN")) ?? [],
    );
    const lotQuantity =
      lotAllocations === undefined
        ? null
        : safeAdd(lotAllocations.map((allocation) => allocation.receivedBaseQuantity));
    if (
      lineIds.has(line.lineId) ||
      movementIds.has(line.movementId) ||
      line.receivedBaseQuantityAfter !==
        line.receivedBaseQuantityBefore + line.receivedBaseQuantity ||
      line.orderedBaseQuantity !== line.orderedInputUnitQuantity * line.baseUnitQuantityPerInput ||
      !Number.isSafeInteger(line.orderedBaseQuantity) ||
      (line.conversionId === null) !== (line.conversionVersion === null) ||
      (line.conversionId === null &&
        (line.baseUnitQuantityPerInput !== 1 || line.inputUnitName !== line.baseUnitName)) ||
      (line.conversionId !== null &&
        (line.baseUnitQuantityPerInput < 2 ||
          line.inputUnitName.toLocaleLowerCase("zh-CN") ===
            line.baseUnitName.toLocaleLowerCase("zh-CN"))) ||
      line.remainingBaseQuantityAfter !==
        line.orderedBaseQuantity - line.receivedBaseQuantityAfter ||
      line.receivedBaseQuantityAfter > line.orderedBaseQuantity ||
      line.orderedTotalFen !== line.orderedSubtotalFen + line.orderedTaxFen ||
      line.subtotalFen !==
        allocatePurchaseReceiptFen(
          line.orderedSubtotalFen,
          line.orderedBaseQuantity,
          line.receivedBaseQuantityBefore,
          line.receivedBaseQuantityAfter,
        ) ||
      line.taxFen !==
        allocatePurchaseReceiptFen(
          line.orderedTaxFen,
          line.orderedBaseQuantity,
          line.receivedBaseQuantityBefore,
          line.receivedBaseQuantityAfter,
        ) ||
      line.totalFen !== line.subtotalFen + line.taxFen ||
      line.inventoryValueIncreaseFen !== line.subtotalFen ||
      line.inventoryQuantityAfter !== line.inventoryQuantityBefore + line.receivedBaseQuantity ||
      line.inventoryVersionAfter !== line.inventoryVersionBefore + 1 ||
      !isPurchaseInventoryValuationSnapshot(line.valuationBefore) ||
      !isPurchaseInventoryValuationSnapshot(line.valuationAfter) ||
      line.valuationBefore.quantity !== line.inventoryQuantityBefore ||
      line.valuationAfter.quantity !== line.inventoryQuantityAfter ||
      !valuationSourceMatchesBefore(line.valuationBeforeSource, line.valuationBefore) ||
      line.valuationAfterSource.receiptId !== receipt.receiptId ||
      line.valuationAfter.quantity !== line.valuationBefore.quantity + line.receivedBaseQuantity ||
      line.valuationAfter.totalValueFen !==
        line.valuationBefore.totalValueFen + line.inventoryValueIncreaseFen ||
      (lotAllocations !== undefined &&
        (lotIds.size !== lotAllocations.length ||
          lotNumbers.size !== lotAllocations.length ||
          lotQuantity !== line.receivedBaseQuantity ||
          !lotAllocations.every(isInventoryLotReceiptAllocationRecord)))
    ) {
      return false;
    }
    lineIds.add(line.lineId);
    movementIds.add(line.movementId);
    remaining += line.remainingBaseQuantityAfter;
    if (!Number.isSafeInteger(remaining)) return false;
  }
  const subtotalFen = safeAdd(receipt.lines.map((line) => line.subtotalFen));
  const taxFen = safeAdd(receipt.lines.map((line) => line.taxFen));
  const totalFen = safeAdd(receipt.lines.map((line) => line.totalFen));
  const complete = receipt.purchaseOrderRemainingBaseQuantityAfter === 0;
  return (
    subtotalFen !== null &&
    taxFen !== null &&
    totalFen !== null &&
    receipt.subtotalFen === subtotalFen &&
    receipt.taxFen === taxFen &&
    receipt.totalFen === totalFen &&
    receipt.totalFen === receipt.subtotalFen + receipt.taxFen &&
    receipt.purchaseOrderRemainingBaseQuantityAfter >= remaining &&
    receipt.purchaseOrderStatusAfter === (complete ? "RECEIVED" : "PARTIALLY_RECEIVED") &&
    receipt.inventoryEffect === (complete ? "FULL" : "PARTIAL")
  );
}

export function isPurchaseOrderShortCloseRecord(
  value: unknown,
): value is PurchaseOrderShortCloseRecord {
  if (!Value.Check(PurchaseOrderShortCloseRecordSchema, value)) return false;
  const record = value as PurchaseOrderShortCloseRecord;
  let expectedNumber: string;
  try {
    expectedNumber = derivePurchaseOrderShortCloseNumber(record.businessDate, record.shortCloseId);
  } catch {
    return false;
  }
  if (
    record.shortCloseNumber !== expectedNumber ||
    record.purchaseOrderVersionAfter !== record.purchaseOrderVersionBefore + 1 ||
    !isCanonicalBusinessDate(record.businessDate) ||
    !isCanonicalBusinessDate(record.lastReceiptBusinessDate) ||
    record.businessDate < record.lastReceiptBusinessDate ||
    !isCanonicalTimestamp(record.closedAt) ||
    record.reason !== record.reason.trim() ||
    record.inventoryEffect !== "NONE" ||
    record.payableEffect !== "NONE" ||
    record.payableTotalFenAfter !== record.payableTotalFenBefore ||
    !traceMatchesPurchaseOrder(
      record.operationTrace,
      record.purchaseOrderId,
      record.purchaseOrderNumber,
      record.operationTrace.idempotencyKey,
    )
  ) {
    return false;
  }
  const lineIds = new Set<string>();
  for (const line of record.lines) {
    if (
      lineIds.has(line.lineId) ||
      line.orderedBaseQuantity !== line.receivedBaseQuantity + line.shortClosedBaseQuantity ||
      line.orderedTotalFen !== line.orderedSubtotalFen + line.orderedTaxFen ||
      line.receivedTotalFen !== line.receivedSubtotalFen + line.receivedTaxFen ||
      line.shortClosedSubtotalFen !== line.orderedSubtotalFen - line.receivedSubtotalFen ||
      line.shortClosedTaxFen !== line.orderedTaxFen - line.receivedTaxFen ||
      line.shortClosedTotalFen !== line.shortClosedSubtotalFen + line.shortClosedTaxFen
    ) {
      return false;
    }
    lineIds.add(line.lineId);
  }
  const shortClosedBaseQuantity = safeAdd(record.lines.map((line) => line.shortClosedBaseQuantity));
  const shortClosedSubtotalFen = safeAdd(record.lines.map((line) => line.shortClosedSubtotalFen));
  const shortClosedTaxFen = safeAdd(record.lines.map((line) => line.shortClosedTaxFen));
  const shortClosedTotalFen = safeAdd(record.lines.map((line) => line.shortClosedTotalFen));
  return (
    shortClosedBaseQuantity !== null &&
    shortClosedBaseQuantity > 0 &&
    shortClosedSubtotalFen !== null &&
    shortClosedTaxFen !== null &&
    shortClosedTotalFen !== null &&
    record.shortClosedBaseQuantity === shortClosedBaseQuantity &&
    record.shortClosedSubtotalFen === shortClosedSubtotalFen &&
    record.shortClosedTaxFen === shortClosedTaxFen &&
    record.shortClosedTotalFen === shortClosedTotalFen
  );
}

export function isPurchaseOrderShortCloseWriteResult(
  value: unknown,
): value is PurchaseOrderShortCloseWriteResult {
  return (
    Value.Check(PurchaseOrderShortCloseWriteResultSchema, value) &&
    isPurchaseOrderShortCloseRecord((value as PurchaseOrderShortCloseWriteResult).shortClose)
  );
}

export function isPurchaseReceiptWriteResult(value: unknown): value is PurchaseReceiptWriteResult {
  return (
    Value.Check(PurchaseReceiptWriteResultSchema, value) &&
    isPurchaseReceiptRecord((value as PurchaseReceiptWriteResult).receipt)
  );
}

export function isPurchaseOrderReceivingWorkspace(
  value: unknown,
): value is PurchaseOrderReceivingWorkspace {
  if (!Value.Check(PurchaseOrderReceivingWorkspaceSchema, value)) return false;
  const workspace = value as PurchaseOrderReceivingWorkspace;
  if (!isCanonicalTimestamp(workspace.generatedAt)) return false;
  const lineIds = new Set<string>();
  let remainingBaseQuantity = 0;
  let receivedBaseQuantity = 0;
  for (const line of workspace.lines) {
    if (
      lineIds.has(line.lineId) ||
      line.receivedBaseQuantity > line.orderedBaseQuantity ||
      line.orderedBaseQuantity !== line.inputUnitQuantity * line.baseUnitQuantityPerInput ||
      !Number.isSafeInteger(line.orderedBaseQuantity) ||
      (line.conversionId === null) !== (line.conversionVersion === null) ||
      (line.conversionId === null &&
        (line.baseUnitQuantityPerInput !== 1 || line.inputUnitName !== line.baseUnitName)) ||
      (line.conversionId !== null &&
        (line.baseUnitQuantityPerInput < 2 ||
          line.inputUnitName.toLocaleLowerCase("zh-CN") ===
            line.baseUnitName.toLocaleLowerCase("zh-CN"))) ||
      line.remainingBaseQuantity !== line.orderedBaseQuantity - line.receivedBaseQuantity ||
      line.orderedTotalFen !== line.orderedSubtotalFen + line.orderedTaxFen ||
      line.receivedTotalFen !== line.receivedSubtotalFen + line.receivedTaxFen ||
      line.receivedSubtotalFen > line.orderedSubtotalFen ||
      line.receivedTaxFen > line.orderedTaxFen ||
      line.receivedTotalFen > line.orderedTotalFen
    ) {
      return false;
    }
    lineIds.add(line.lineId);
    remainingBaseQuantity += line.remainingBaseQuantity;
    receivedBaseQuantity += line.receivedBaseQuantity;
    if (
      !Number.isSafeInteger(remainingBaseQuantity) ||
      !Number.isSafeInteger(receivedBaseQuantity)
    ) {
      return false;
    }
  }
  const orderedSubtotalFen = safeAdd(workspace.lines.map((line) => line.orderedSubtotalFen));
  const orderedTaxFen = safeAdd(workspace.lines.map((line) => line.orderedTaxFen));
  const orderedTotalFen = safeAdd(workspace.lines.map((line) => line.orderedTotalFen));
  const receivedSubtotalFen = safeAdd(workspace.lines.map((line) => line.receivedSubtotalFen));
  const receivedTaxFen = safeAdd(workspace.lines.map((line) => line.receivedTaxFen));
  const receivedTotalFen = safeAdd(workspace.lines.map((line) => line.receivedTotalFen));
  const receiptIds = new Set<string>();
  const receiptNumbers = new Set<string>();
  let latestReceiptBusinessDate: string | null = null;
  for (const result of workspace.receipts) {
    if (
      !isPurchaseReceiptWriteResult(result) ||
      result.receipt.purchaseOrderId !== workspace.purchaseOrderId ||
      result.receipt.purchaseOrderNumber !== workspace.purchaseOrderNumber ||
      result.receipt.approvalId !== workspace.approvalId ||
      result.receipt.approvalSnapshotSha256 !== workspace.approvalSnapshotSha256 ||
      receiptIds.has(result.receipt.receiptId) ||
      receiptNumbers.has(result.receipt.receiptNumber)
    ) {
      return false;
    }
    receiptIds.add(result.receipt.receiptId);
    receiptNumbers.add(result.receipt.receiptNumber);
    if (
      latestReceiptBusinessDate === null ||
      result.receipt.businessDate > latestReceiptBusinessDate
    ) {
      latestReceiptBusinessDate = result.receipt.businessDate;
    }
  }
  const expectedStatus =
    remainingBaseQuantity === 0
      ? "RECEIVED"
      : receivedBaseQuantity === 0
        ? "APPROVED"
        : "PARTIALLY_RECEIVED";
  const shortClose = workspace.shortClose;
  if (shortClose !== null) {
    if (
      !isPurchaseOrderShortCloseRecord(shortClose) ||
      expectedStatus !== "PARTIALLY_RECEIVED" ||
      shortClose.purchaseOrderId !== workspace.purchaseOrderId ||
      shortClose.purchaseOrderNumber !== workspace.purchaseOrderNumber ||
      shortClose.approvalId !== workspace.approvalId ||
      shortClose.approvalSnapshotSha256 !== workspace.approvalSnapshotSha256 ||
      shortClose.purchaseOrderVersionAfter !== workspace.purchaseOrderVersion ||
      shortClose.purchaseOrderStatusAfter !== workspace.status ||
      latestReceiptBusinessDate === null ||
      shortClose.lastReceiptBusinessDate !== latestReceiptBusinessDate ||
      shortClose.supplier.supplierId !== workspace.supplier.supplierId ||
      shortClose.supplier.supplierCode !== workspace.supplier.supplierCode ||
      shortClose.supplier.name !== workspace.supplier.name ||
      shortClose.supplier.version !== workspace.supplier.version ||
      shortClose.supplier.paymentTermsDays !== workspace.supplier.paymentTermsDays ||
      shortClose.payableTotalFenBefore !== workspace.payableTotalFen ||
      shortClose.payableTotalFenAfter !== workspace.payableTotalFen ||
      shortClose.operationTrace.commandId !== workspace.lastCommandId ||
      shortClose.lines.length !== workspace.lines.length
    ) {
      return false;
    }
    const workspaceLineById = new Map(workspace.lines.map((line) => [line.lineId, line]));
    for (const line of shortClose.lines) {
      const current = workspaceLineById.get(line.lineId);
      if (
        current === undefined ||
        line.skuId !== current.skuId ||
        line.productName !== current.productName ||
        line.baseUnitName !== current.baseUnitName ||
        line.orderedBaseQuantity !== current.orderedBaseQuantity ||
        line.receivedBaseQuantity !== current.receivedBaseQuantity ||
        line.shortClosedBaseQuantity !== current.remainingBaseQuantity ||
        line.orderedSubtotalFen !== current.orderedSubtotalFen ||
        line.orderedTaxFen !== current.orderedTaxFen ||
        line.orderedTotalFen !== current.orderedTotalFen ||
        line.receivedSubtotalFen !== current.receivedSubtotalFen ||
        line.receivedTaxFen !== current.receivedTaxFen ||
        line.receivedTotalFen !== current.receivedTotalFen
      ) {
        return false;
      }
    }
  }
  return (
    orderedSubtotalFen !== null &&
    orderedTaxFen !== null &&
    orderedTotalFen !== null &&
    receivedSubtotalFen !== null &&
    receivedTaxFen !== null &&
    receivedTotalFen !== null &&
    workspace.orderedSubtotalFen === orderedSubtotalFen &&
    workspace.orderedTaxFen === orderedTaxFen &&
    workspace.orderedTotalFen === orderedTotalFen &&
    workspace.receivedSubtotalFen === receivedSubtotalFen &&
    workspace.receivedTaxFen === receivedTaxFen &&
    workspace.receivedTotalFen === receivedTotalFen &&
    workspace.payableTotalFen === workspace.receivedTotalFen &&
    workspace.status === (shortClose === null ? expectedStatus : "CLOSED") &&
    workspace.canReceive === (shortClose === null && remainingBaseQuantity > 0) &&
    workspace.canCloseShort === (shortClose === null && expectedStatus === "PARTIALLY_RECEIVED") &&
    (workspace.canReceive || workspace.canCloseShort
      ? workspace.blocker === null
      : workspace.blocker !== null)
  );
}

export function isPurchaseOrderReceivingWorkspaceList(
  value: unknown,
): value is PurchaseOrderReceivingWorkspace[] {
  if (!Value.Check(PurchaseOrderReceivingWorkspaceListSchema, value)) return false;
  const ids = new Set<string>();
  const numbers = new Set<string>();
  for (const workspace of value as PurchaseOrderReceivingWorkspace[]) {
    if (
      !isPurchaseOrderReceivingWorkspace(workspace) ||
      ids.has(workspace.purchaseOrderId) ||
      numbers.has(workspace.purchaseOrderNumber)
    ) {
      return false;
    }
    ids.add(workspace.purchaseOrderId);
    numbers.add(workspace.purchaseOrderNumber);
  }
  return true;
}

export function isPurchaseReceiptBusinessEventPayload(
  value: unknown,
): value is PurchaseReceiptBusinessEventPayload {
  return (
    Value.Check(PurchaseReceiptBusinessEventPayloadSchema, value) &&
    isPurchaseReceiptRecord((value as PurchaseReceiptBusinessEventPayload).receipt)
  );
}

export function isPurchaseOrderShortClosedBusinessEventPayload(
  value: unknown,
): value is PurchaseOrderShortClosedBusinessEventPayload {
  return (
    Value.Check(PurchaseOrderShortClosedBusinessEventPayloadSchema, value) &&
    isPurchaseOrderShortCloseRecord(
      (value as PurchaseOrderShortClosedBusinessEventPayload).shortClose,
    )
  );
}
